import { Link } from 'react-router-dom';
import { Scale, Hammer, ShieldCheck, Handshake, UserPlus, Building2, Radar, ShieldAlert, ArrowRight, BadgeCheck, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/common/Primitives';
import { MODERATION_RULES } from '@/lib/moderation';
import { useSeo } from '@/lib/seo';

export const HowItWorks = () => (
  <div className="grid gap-4 md:grid-cols-3" data-testid="how-it-works">
    {[
      { n: '01', icon: UserPlus, t: 'Crie a conta e o perfil', d: 'Cadastre sua empresa com categoria, localização, serviços e canais oficiais.' },
      { n: '02', icon: Building2, t: 'Apareça no mapa', d: 'Seu negócio passa a ser encontrado por estado, cidade, categoria e palavra-chave.' },
      { n: '03', icon: Radar, t: 'Publique e responda oportunidades', d: 'Procure fornecedores, distribuidores, representantes e parceiros — ou ofereça seus serviços.' },
    ].map(({ n, icon: Icon, t, d }) => (
      <div key={n} className="surface p-5">
        <div className="flex items-center justify-between"><Icon className="h-5 w-5 text-primary" /><span className="font-mono text-xs text-muted-foreground">{n}</span></div>
        <div className="mt-4 font-display text-base font-semibold">{t}</div>
        <p className="mt-1.5 text-sm text-muted-foreground">{d}</p>
      </div>
    ))}
  </div>
);

export const ModerationRules = () => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="moderation-rules">
    {MODERATION_RULES.map((r) => (
      <div key={r.id} className="surface p-4">
        <div className="flex items-center gap-2 text-sm font-semibold"><ShieldAlert className="h-4 w-4 text-[#ff6b84]" /> {r.title}</div>
        <p className="mt-1.5 text-sm text-muted-foreground">{r.text}</p>
      </div>
    ))}
  </div>
);

export default function About() {
  useSeo({ title: 'Sobre a plataforma', description: 'Propósito, valores e regras da rede Brasil Livre Business.' });
  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="pointer-events-none absolute inset-0 hero-glow" />
        <div className="container-x relative py-14 sm:py-20">
          <div className="eyebrow">Sobre a plataforma</div>
          <h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">Uma rede para quem acredita que o Brasil cresce pelo trabalho e pela livre iniciativa.</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
            O Brasil Livre Business conecta empresários, empreendedores e prestadores de serviço que compartilham valores conservadores — liberdade econômica, responsabilidade individual e valorização do trabalho — para gerar negócios reais entre si. Nosso foco é comercial: diretório, oportunidades e relacionamento profissional, não notícias ou disputas partidárias.
          </p>
        </div>
      </section>
      <section className="container-x py-14">
        <SectionHeader eyebrow="Princípios" title="O que nos orienta" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Scale, t: 'Liberdade econômica', d: 'Defendemos um ambiente de negócios aberto, com menos burocracia e mais concorrência leal.' },
            { icon: Hammer, t: 'Valorização do trabalho', d: 'Quem produz, emprega e inova merece visibilidade e reconhecimento.' },
            { icon: ShieldCheck, t: 'Responsabilidade', d: 'Palavra cumprida, contratos respeitados e informações verdadeiras.' },
            { icon: Handshake, t: 'Cooperação', d: 'Empresas da mesma comunidade fazendo negócios e indicando umas às outras.' },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="surface p-5"><Icon className="h-5 w-5 text-primary" /><div className="mt-3 font-display font-semibold">{t}</div><p className="mt-1.5 text-sm text-muted-foreground">{d}</p></div>
          ))}
        </div>
      </section>
      <section className="container-x py-6" id="como-funciona">
        <SectionHeader eyebrow="Passo a passo" title="Como funciona" />
        <HowItWorks />
      </section>
      <section className="container-x py-14">
        <SectionHeader eyebrow="Segurança da rede" title="Critérios de moderação" text="Publicações passam por verificações automáticas e podem ser denunciadas por qualquer visitante. Violações levam à remoção de conteúdo e suspensão de contas." right={<Button asChild variant="secondary" className="border border-white/10"><Link to="/diretrizes">Diretrizes completas <ArrowRight /></Link></Button>} />
        <ModerationRules />
      </section>
      <section className="container-x pb-6">
        <SectionHeader eyebrow="Roadmap" title="Próximas etapas" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="surface p-5"><BadgeCheck className="h-5 w-5 text-primary" /><div className="mt-3 font-display font-semibold">Verificação de empresas</div><p className="mt-1.5 text-sm text-muted-foreground">O CNPJ já é validado na Receita Federal. A checagem documental para o selo completo será ativada em breve.</p></div>
          <div className="surface p-5"><Rocket className="h-5 w-5 text-primary" /><div className="mt-3 font-display font-semibold">Planos e destaques</div><p className="mt-1.5 text-sm text-muted-foreground">Perfis premium e publicações patrocinadas, sempre identificadas. Preços e condições ainda em definição.</p></div>
        </div>
      </section>
    </div>
  );
}